import { logError, logWarn } from './Logger.js';
import { isSSR } from '#shared/utils/isSSR';
import buildMockFileName from '#shared/utils/buildMockFileName';
import { fallbackTypes } from '#shared/constants';

/**
 * Configuration for the Xapi fallback mechanism.
 * This object centralizes settings for enabling/disabling the feature and file paths.
 */
const config = {
  /** A flag to enable or disable the fallback. If false, no mock data will be loaded on API failure. */
  enabled: true,
};

/**
 * Fetches fallback mock data for a given type (header or footer) when an Xapi call fails.
 * It dynamically constructs the import path for the mock file based on whether
 * the code is running on the server (SSR) or the client.
 *
 * @param {object} params - Parameters to build the mock file name (e.g., isMobile, isInternational).
 * @param {typeof fallbackTypes[keyof typeof fallbackTypes]} type - The type of content to get a fallback for.
 * @param {boolean} isDesktopOnly - A flag to indicate if the view is desktop-only.
 * @returns {Promise<{value: object}|null>} A promise that resolves to an object containing the mock data, or null if fallback is disabled or fails.
 */
async function getFallback(params, type, isDesktopOnly) {
  if (!config.enabled) {
    return null;
  }

  const context = {
    ...params,
    isDesktopOnly,
  };

  const fileName = buildMockFileName(context);
  let mock = { default: null };

  try {
    if (isSSR()) {
      try {
        mock = await import(/* @vite-ignore */ `${process.cwd()}/mocks/fallback/${type}/${fileName}.js`);
      } catch (error) {
        logWarn(`SSR: Failed to import ${type} mock data from "${fileName}.js"`, error);
      }
    } else {
      try {
        // IMPORTANT: static `../../mocks/fallback/` is needed for client side dynamic
        // import pattern to allows Vite to automatically find, bundle, and create
        // optimized "chunks" for all possible mock files. At runtime, the browser will
        // only fetch the specific chunk it needs.
        mock = await import(`../../mocks/fallback/${type}/${fileName}.js`);
      } catch (error) {
        logWarn(`CLIENT: [EXPECTED] failure from import ${type} mock data "${fileName}.js" with absolutePath`, error);
        mock = { default: {} };
      }
    }
    logWarn(`Now using ${type} mock data from "${fileName}.js"`);
  } catch (error) {
    logError(`${isSSR() ? 'SSR:' : 'CLIENT:'} Failed to load fallback mock "${fileName}.js" for ${type}.`, error);
    return null;
  }

  return mock.default ? { value: mock.default } : null;
}

/**
 * Retrieves the fallback mock data for the header.
 * @param {object} params - Parameters for building the mock file name.
 * @returns {Promise<{value: object}|null>} A promise that resolves to the header mock data.
 */
const getHeaderFallback = (params) => getFallback(params, fallbackTypes.header, false);

/**
 * Retrieves the fallback mock data for the footer.
 * @param {object} params - Parameters for building the mock file name.
 * @returns {Promise<{value: object}|null>} A promise that resolves to the footer mock data.
 */
const getFooterFallback = (params) => getFallback(params, fallbackTypes.footer, true);

export { getHeaderFallback, getFooterFallback };
